import * as THREE from 'three';

export class Scene {
  constructor() {
    // Create scene
    this.scene = new THREE.Scene();
    
    // Create renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    
    // Add renderer to page
    document.body.appendChild(this.renderer.domElement);
    
    // Create camera
    this.createCamera();
    
    // Create lighting
    this.createLighting();
    
    // Fog for depth perception
    this.scene.fog = new THREE.FogExp2(0x92c5de, 0.002);
    
    // Create portals if URL parameter is present
    this.createPortals();
  }
  
  createCamera() {
    // Use perspective camera for 3D view
    this.camera = new THREE.PerspectiveCamera(
      75, // FOV
      window.innerWidth / window.innerHeight, // Aspect ratio
      0.1, // Near plane
      1000 // Far plane
    );
    
    // Position camera behind and above the starting position
    this.camera.position.set(0, 15, -15);
    this.camera.lookAt(0, 0, 0);
    
    // Camera follow variables - ADJUSTED for better view behind the car
    this.cameraFollow = {
      distance: 10, // Changed from 15 to 10 for closer view
      height: 5, // Changed from 10 to 5 for lower view
      lookAtHeight: 1, // Changed from 2 to 1 to look more directly at car
      lerpFactor: 0.05, // Reduced from 0.1 to 0.05 for smoother camera
      target: null
    };
  }
  
  createLighting() {
    // Create directional light (like sunlight)
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    this.directionalLight.position.set(100, 100, 100);
    this.directionalLight.castShadow = true;
    
    // Configure shadow properties
    this.directionalLight.shadow.mapSize.width = 2048;
    this.directionalLight.shadow.mapSize.height = 2048;
    this.directionalLight.shadow.camera.near = 0.5;
    this.directionalLight.shadow.camera.far = 500;
    this.directionalLight.shadow.camera.left = -100;
    this.directionalLight.shadow.camera.right = 100;
    this.directionalLight.shadow.camera.top = 100;
    this.directionalLight.shadow.camera.bottom = -100;
    
    this.scene.add(this.directionalLight);
    
    // Add ambient light for overall illumination
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(this.ambientLight);
  }
  
  createPortals() {
    // Check if portal parameter is present in URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasPortalParam = urlParams.get('portal') === 'true';
    const refUrl = urlParams.get('ref');

    // Create exit portal (always present) to send player to portal.pieter.com
    this.createExitPortal();
    
    // Create entrance portal only if coming from another game
    if (hasPortalParam && refUrl) {
      this.createEntrancePortal(refUrl);
    }
  }
  
  createEntrancePortal(refUrl) {
    // Create portal group to contain all portal elements
    const startPortalGroup = new THREE.Group();
    startPortalGroup.position.set(0, 2, 0); // Place at spawn point
    startPortalGroup.rotation.x = 0.35;
    startPortalGroup.rotation.y = 0;

    // Create portal effect - REDUCED SIZE
    const startPortalGeometry = new THREE.TorusGeometry(5, 0.5, 16, 100); // Reduced from 15,2 to 5,0.5
    const startPortalMaterial = new THREE.MeshPhongMaterial({
        color: 0xff0000,
        emissive: 0xff0000,
        transparent: true,
        opacity: 0.8
    });
    const startPortal = new THREE.Mesh(startPortalGeometry, startPortalMaterial);
    startPortalGroup.add(startPortal);
                            
    // Create portal inner surface - REDUCED SIZE
    const startPortalInnerGeometry = new THREE.CircleGeometry(4.5, 32); // Reduced from 13 to 4.5
    const startPortalInnerMaterial = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide
    });
    const startPortalInner = new THREE.Mesh(startPortalInnerGeometry, startPortalInnerMaterial);
    startPortalGroup.add(startPortalInner);

    // Create particle system for portal effect - ADJUSTED FOR SMALLER SIZE
    const startPortalParticleCount = 500; // Reduced particle count
    const startPortalParticles = new THREE.BufferGeometry();
    const startPortalPositions = new Float32Array(startPortalParticleCount * 3);
    const startPortalColors = new Float32Array(startPortalParticleCount * 3);

    for (let i = 0; i < startPortalParticleCount * 3; i += 3) {
        // Create particles in a smaller ring around the portal
        const angle = Math.random() * Math.PI * 2;
        const radius = 5 + (Math.random() - 0.5) * 1; // Reduced from 15 to 5
        startPortalPositions[i] = Math.cos(angle) * radius;
        startPortalPositions[i + 1] = Math.sin(angle) * radius;
        startPortalPositions[i + 2] = (Math.random() - 0.5) * 1; // Reduced spread

        // Red color with slight variation
        startPortalColors[i] = 0.8 + Math.random() * 0.2;
        startPortalColors[i + 1] = 0;
        startPortalColors[i + 2] = 0;
    }

    startPortalParticles.setAttribute('position', new THREE.BufferAttribute(startPortalPositions, 3));
    startPortalParticles.setAttribute('color', new THREE.BufferAttribute(startPortalColors, 3));

    const startPortalParticleMaterial = new THREE.PointsMaterial({
        size: 0.2,
        vertexColors: true,
        transparent: true,
        opacity: 0.6
    });

    const startPortalParticleSystem = new THREE.Points(startPortalParticles, startPortalParticleMaterial);
    startPortalGroup.add(startPortalParticleSystem);

    // Add portal group to scene
    this.scene.add(startPortalGroup);

    // Store for collision detection and animation
    this.entrancePortal = {
        group: startPortalGroup,
        ring: startPortal,
        inner: startPortalInner,
        particles: startPortalParticleSystem,
        particlesGeometry: startPortalParticles,
        box: new THREE.Box3().setFromObject(startPortalGroup),
        refUrl: refUrl
    };
  }
  
  createExitPortal() {
    // Create portal group to contain all portal elements
    const exitPortalGroup = new THREE.Group();
    exitPortalGroup.position.set(50, 2, 40);
    exitPortalGroup.rotation.x = 0.35;
    exitPortalGroup.rotation.y = -Math.PI / 4;

    // Create portal effect - REDUCED SIZE
    const exitPortalGeometry = new THREE.TorusGeometry(5, 0.5, 16, 100); // Reduced from 15,2 to 5,0.5
    const exitPortalMaterial = new THREE.MeshPhongMaterial({
        color: 0x00ff00,
        emissive: 0x00ff00,
        transparent: true,
        opacity: 0.8
    });
    const exitPortal = new THREE.Mesh(exitPortalGeometry, exitPortalMaterial);
    exitPortalGroup.add(exitPortal);

    // Create portal inner surface - REDUCED SIZE
    const exitPortalInnerGeometry = new THREE.CircleGeometry(4.5, 32); // Reduced from 13 to 4.5
    const exitPortalInnerMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide
    });
    const exitPortalInner = new THREE.Mesh(exitPortalInnerGeometry, exitPortalInnerMaterial);
    exitPortalGroup.add(exitPortalInner);
    
    // Add portal label - ADJUSTED SIZE
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 512;
    canvas.height = 64;
    context.fillStyle = '#00ff00';
    context.font = 'bold 32px Arial';
    context.textAlign = 'center';
    context.fillText('VIBEVERSE PORTAL', canvas.width/2, canvas.height/2);
    const texture = new THREE.CanvasTexture(canvas);
    const labelGeometry = new THREE.PlaneGeometry(10, 2); // Reduced from 30,5 to 10,2
    const labelMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide
    });
    const label = new THREE.Mesh(labelGeometry, labelMaterial);
    label.position.y = 7; // Reduced from 20 to 7
    exitPortalGroup.add(label);

    // Create particle system for portal effect - ADJUSTED FOR SMALLER SIZE
    const exitPortalParticleCount = 500; // Reduced particle count
    const exitPortalParticles = new THREE.BufferGeometry();
    const exitPortalPositions = new Float32Array(exitPortalParticleCount * 3);
    const exitPortalColors = new Float32Array(exitPortalParticleCount * 3);

    for (let i = 0; i < exitPortalParticleCount * 3; i += 3) {
        // Create particles in a smaller ring around the portal
        const angle = Math.random() * Math.PI * 2;
        const radius = 5 + (Math.random() - 0.5) * 1; // Reduced from 15 to 5
        exitPortalPositions[i] = Math.cos(angle) * radius;
        exitPortalPositions[i + 1] = Math.sin(angle) * radius;
        exitPortalPositions[i + 2] = (Math.random() - 0.5) * 1; // Reduced spread

        // Green color with slight variation
        exitPortalColors[i] = 0;
        exitPortalColors[i + 1] = 0.8 + Math.random() * 0.2;
        exitPortalColors[i + 2] = 0;
    }

    exitPortalParticles.setAttribute('position', new THREE.BufferAttribute(exitPortalPositions, 3));
    exitPortalParticles.setAttribute('color', new THREE.BufferAttribute(exitPortalColors, 3));

    const exitPortalParticleMaterial = new THREE.PointsMaterial({
        size: 0.2,
        vertexColors: true,
        transparent: true,
        opacity: 0.6
    });

    const exitPortalParticleSystem = new THREE.Points(exitPortalParticles, exitPortalParticleMaterial);
    exitPortalGroup.add(exitPortalParticleSystem);

    // Add portal group to scene
    this.scene.add(exitPortalGroup);

    // Store for collision detection and animation
    this.exitPortal = {
        group: exitPortalGroup,
        ring: exitPortal,
        inner: exitPortalInner,
        particles: exitPortalParticleSystem,
        particlesGeometry: exitPortalParticles,
        box: new THREE.Box3().setFromObject(exitPortalGroup),
        label: label
    };
  }
  
  add(object) {
    this.scene.add(object);
  }
  
  remove(object) {
    if (object && this.scene) {
      this.scene.remove(object);
    }
  }
  
  cleanup() {
    // Clear all objects from the scene
    while(this.scene.children.length > 0) { 
      this.scene.remove(this.scene.children[0]); 
    }
    
    // Dispose of renderer
    if (this.renderer) {
      this.renderer.dispose();
      // Remove canvas from DOM
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
  }
  
  updateCameraFollowTarget(target) {
    // Set camera follow target
    this.cameraFollow.target = target;
  }
  
  animatePortals() {
    // Animate entrance portal if it exists
    if (this.entrancePortal) {
        const positions = this.entrancePortal.particlesGeometry.attributes.position.array;
        for (let i = 0; i < positions.length; i += 3) {
            positions[i + 1] += 0.05 * Math.sin(Date.now() * 0.001 + i);
        }
        this.entrancePortal.particlesGeometry.attributes.position.needsUpdate = true;
    }

    // Animate exit portal
    if (this.exitPortal) {
        const positions = this.exitPortal.particlesGeometry.attributes.position.array;
        for (let i = 0; i < positions.length; i += 3) {
            positions[i + 1] += 0.05 * Math.sin(Date.now() * 0.001 + i);
        }
        this.exitPortal.particlesGeometry.attributes.position.needsUpdate = true;
    }
  }
  
  checkPortalCollisions(player) {
    // Skip if no player or portals
    if (!player || !player.mesh) return null;
    
    // Create player bounding box
    const playerBox = new THREE.Box3().setFromObject(player.mesh);
    
    // Check entrance portal collision
    if (this.entrancePortal) {
      // Update portal bounding box
      this.entrancePortal.box.setFromObject(this.entrancePortal.group);
      
      // Calculate distance to portal
      const distance = playerBox.getCenter(new THREE.Vector3())
        .distanceTo(this.entrancePortal.box.getCenter(new THREE.Vector3()));
      
      // Check for collision/proximity
      if (distance < 3 && playerBox.intersectsBox(this.entrancePortal.box)) {
        // Return to the game this player came from
        const refUrl = this.entrancePortal.refUrl;
        
        if (refUrl) {
          // Format URL properly
          let url = refUrl;
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
          }
          
          // Forward all current parameters except ref
          const currentParams = new URLSearchParams(window.location.search);
          const newParams = new URLSearchParams();
          
          // Add portal=true to indicate we're coming from a portal
          newParams.append('portal', 'true');
          
          // Copy all other parameters except ref
          for (const [key, value] of currentParams) {
            if (key !== 'ref') {
              newParams.append(key, value);
            }
          }
          
          // Add ref to current URL
          newParams.append('ref', window.location.hostname);
          
          const paramString = newParams.toString();
          return {
            type: 'entrance',
            url: url + (paramString ? '?' + paramString : '')
          };
        }
      }
    }
    
    // Check exit portal collision
    if (this.exitPortal) {
      // Update portal bounding box
      this.exitPortal.box.setFromObject(this.exitPortal.group);
      
      // Calculate distance to portal
      const distance = playerBox.getCenter(new THREE.Vector3())
        .distanceTo(this.exitPortal.box.getCenter(new THREE.Vector3()));
      
      // Check for collision/proximity
      if (distance < 3) {
        // Only return URL when player is actually inside the portal
        if (playerBox.intersectsBox(this.exitPortal.box)) {
          // Create URL for portal.pieter.com
          const newParams = new URLSearchParams();
          
          // Add required parameters
          newParams.append('portal', 'true');
          newParams.append('username', this.getPlayerUsername());
          
          // Get player car color
          let carColor = 'blue';
          if (window.game && window.game.playerCarColor) {
            carColor = window.game.playerCarColor;
          }
          newParams.append('color', carColor);
          
          // Add player speed in m/s
          const speed = player.body ? player.body.velocity.length() * 0.1 : 0;
          newParams.append('speed', speed.toFixed(2));
          
          // Add reference to current site
          newParams.append('ref', window.location.hostname);
          
          // Add current game state
          if (window.game && window.game.getGameState) {
            const gameState = window.game.getGameState();
            newParams.append('gameState', JSON.stringify(gameState));
          }
          
          const paramString = newParams.toString();
          return {
            type: 'exit',
            url: 'https://portal.pieter.com' + (paramString ? '?' + paramString : '')
          };
        }
        
        // Return distance for visual effects when near but not in portal
        return {
          type: 'near-exit',
          distance: distance
        };
      }
    }
    
    return null;
  }
  
  getPlayerUsername() {
    // Try to get username from various sources
    // 1. URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const paramUsername = urlParams.get('username');
    if (paramUsername) return paramUsername;
    
    // 2. Game object username if available
    if (window.game && window.game.username) return window.game.username;
    
    // 3. Default to "Player"
    return "Player";
  }
  
  render(delta) {
    // Animate portals
    this.animatePortals();
    
    // Update camera position if following a target
    if (this.cameraFollow.target) {
      this.updateCameraPosition();
    }
    
    // Render the scene
    this.renderer.render(this.scene, this.camera);
  }
  
  resize() {
    // Update camera aspect ratio and projection matrix
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    
    // Update renderer size
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  updateCameraPosition() {
    // Get target position
    const targetPos = this.cameraFollow.target.getPosition();
    const targetQuat = this.cameraFollow.target.getQuaternion();
    
    // Calculate camera position behind the car
    const offset = new THREE.Vector3(0, 0, -this.cameraFollow.distance);
    offset.applyQuaternion(targetQuat);
    
    // Set desired camera position
    const desiredPosition = new THREE.Vector3(
      targetPos.x + offset.x,
      targetPos.y + this.cameraFollow.height,
      targetPos.z + offset.z
    );
    
    // Force camera position immediately on first render
    if (!this._cameraInitialized) {
      this.camera.position.copy(desiredPosition);
      this._cameraInitialized = true;
    } else {
      // Smoothly interpolate current camera position to desired position
      this.camera.position.lerp(desiredPosition, this.cameraFollow.lerpFactor);
    }
    
    // Make the camera look at the car (slightly above it)
    const lookAtPos = new THREE.Vector3(
      targetPos.x,
      targetPos.y + this.cameraFollow.lookAtHeight,
      targetPos.z
    );
    this.camera.lookAt(lookAtPos);
  }
} 